import type { AudioEngine } from "../audio/AudioEngine";
import type { RNG } from "../core/RNG";
import type { UI } from "../ui/UI";
import type { MenuItem } from "../ui/Menu";
import { applySkillOutOfBattle, isFieldUsable } from "../rpg/FieldEffects";
import type { Hero } from "../rpg/Hero";
import type { ItemDef } from "../rpg/Skills";
import type { World } from "../rpg/World";

const MENU_ANCHOR = { right: "6%", top: "14%" };
const SUB_ANCHOR = { right: "26%", top: "14%" };

/**
 * The pause menu: status, items, spells, equipment. Driven entirely by awaited
 * UI menus, so the whole flow reads top to bottom.
 */
export class PartyMenu {
  constructor(
    private world: World,
    private ui: UI,
    private rng: RNG,
    private audio: AudioEngine,
  ) {}

  async open(): Promise<void> {
    for (;;) {
      const choice = await this.ui.menu().open({
        title: "Menu",
        items: [
          { label: "Status" },
          { label: "Items", detail: String(this.world.party.items.length) },
          { label: "Spells" },
          { label: "Equip" },
          { label: "Close" },
        ],
        anchor: MENU_ANCHOR,
      });

      switch (choice) {
        case 0:
          await this.showStatus();
          break;
        case 1:
          await this.useItems();
          break;
        case 2:
          await this.castSpell();
          break;
        case 3:
          await this.equip();
          break;
        default:
          return;
      }
    }
  }

  // ------------------------------------------------------------------ pages

  private async showStatus(): Promise<void> {
    const hero = await this.chooseMember("Status");
    if (!hero) return;
    const s = hero.stats;
    const weapon = hero.equipment.weapon?.name ?? "—";
    const armor = hero.equipment.armor?.name ?? "—";
    await this.ui.message.show(
      `${hero.name}  Level ${hero.level}\n` +
        `HP ${hero.hp}/${s.maxHp}   MP ${hero.mp}/${s.maxMp}   ` +
        `Attack ${s.attack}   Defense ${s.defense}   Agility ${s.agility}\n` +
        `Weapon: ${weapon}   Armor: ${armor}   Next level: ${hero.expToNext} exp`,
      { speed: 0 },
    );
  }

  private async useItems(): Promise<void> {
    const party = this.world.party;
    if (party.items.length === 0) {
      await this.ui.message.show("The party carries nothing at all.");
      return;
    }

    for (;;) {
      const slots = party.items;
      if (slots.length === 0) return;

      const items: MenuItem[] = slots.map((slot) => {
        const def = this.world.items.get(slot.id);
        const usable = def.use ? isFieldUsable(this.world.skills.get(def.use)) : false;
        return { label: def.name, detail: `x${slot.count}`, disabled: !usable && !def.attack && !def.defense };
      });

      const index = await this.ui.menu().open({ title: "Items", items, anchor: SUB_ANCHOR });
      if (index === null) return;

      const def = this.world.items.get(slots[index].id);
      if (def.use) {
        const skill = this.world.skills.get(def.use);
        const target = await this.chooseMember(def.name);
        if (!target) continue;
        party.removeItem(def.id);
        this.audio.play(skill.sound ?? "heal");
        await this.ui.message.show(applySkillOutOfBattle(skill, target, target, this.rng));
      } else {
        await this.ui.message.show(def.description);
      }
    }
  }

  private async castSpell(): Promise<void> {
    const hero = await this.chooseMember("Spells");
    if (!hero) return;

    const spells = hero.skills
      .map((id) => this.world.skills.get(id))
      .filter((skill) => isFieldUsable(skill));

    if (spells.length === 0) {
      await this.ui.message.show(`${hero.name} knows no spells for a moment like this.`);
      return;
    }

    const index = await this.ui.menu().open({
      title: hero.name,
      items: spells.map((s) => ({
        label: s.name,
        detail: `${s.mp} MP`,
        disabled: hero.mp < s.mp,
      })),
      anchor: SUB_ANCHOR,
    });
    if (index === null) return;

    const skill = spells[index];
    const target = await this.chooseMember(skill.name);
    if (!target) return;

    hero.spendMp(skill.mp);
    this.audio.play(skill.sound ?? "spell");
    await this.ui.message.show(`${hero.name} casts ${skill.name}!`);
    await this.ui.message.show(applySkillOutOfBattle(skill, hero, target, this.rng));
  }

  private async equip(): Promise<void> {
    const hero = await this.chooseMember("Equip");
    if (!hero) return;

    for (;;) {
      const slot = await this.ui.menu().open({
        title: hero.name,
        items: [
          { label: "Weapon", detail: hero.equipment.weapon?.name ?? "—" },
          { label: "Armor", detail: hero.equipment.armor?.name ?? "—" },
        ],
        anchor: SUB_ANCHOR,
      });
      if (slot === null) return;

      const kind = slot === 0 ? "weapon" : "armor";
      const owned = this.world.party.items
        .map((s) => this.world.items.get(s.id))
        .filter((def) => def.kind === kind);

      const index = await this.ui.menu().open({
        title: kind === "weapon" ? "Weapons" : "Armor",
        items: [
          { label: "(remove)" },
          ...owned.map((def) => ({
            label: def.name,
            detail: kind === "weapon" ? `Atk +${def.attack ?? 0}` : `Def +${def.defense ?? 0}`,
          })),
        ],
        anchor: { right: "46%", top: "14%" },
      });
      if (index === null) continue;

      const current: ItemDef | undefined = hero.equipment[kind];
      if (current) this.world.party.addItem(current.id);

      if (index === 0) {
        hero.equipment[kind] = undefined;
        await this.ui.message.show(`${hero.name} puts the ${current?.name ?? "gear"} away.`);
      } else {
        const chosen = owned[index - 1];
        this.world.party.removeItem(chosen.id);
        hero.equipment[kind] = chosen;
        this.audio.play("confirm");
        await this.ui.message.show(`${hero.name} equips the ${chosen.name}.`);
      }
    }
  }

  // ----------------------------------------------------------------- helpers

  private async chooseMember(title: string): Promise<Hero | null> {
    const members = this.world.party.members;
    if (members.length === 1) return members[0];

    const index = await this.ui.menu().open({
      title,
      items: members.map((m) => ({
        label: m.name,
        detail: `HP ${m.hp}/${m.maxHp}`,
        disabled: false,
      })),
      anchor: SUB_ANCHOR,
    });
    return index === null ? null : members[index];
  }
}
